'use client'
import { useEffect } from 'react'
import { reportClientError } from '@/utils/clientErrorReporter'
import { CHUNK_RELOAD_SETTLE_MS, settleChunkReload } from '@/utils/chunkReload'

// Reports uncaught window errors and unhandled promise rejections — failures
// that happen outside the React render tree (event handlers, async code),
// which error boundaries cannot catch. Renders nothing.
const ClientErrorReporter = () => {
  useEffect(() => {
    const onError = (event: ErrorEvent) => {
      reportClientError({
        message: event.message || 'Unknown window error',
        stack: event.error instanceof Error ? event.error.stack : undefined,
        // A webpack ChunkLoadError is a real Error instance — the runtime
        // mutates a plain `new Error()` — so this guard captures its name too
        errorName: event.error instanceof Error ? event.error.name : undefined,
        source: 'window-error',
      })
    }
    const onRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason
      reportClientError({
        message:
          reason instanceof Error
            ? reason.message
            : String(reason ?? 'Unknown rejection'),
        stack: reason instanceof Error ? reason.stack : undefined,
        errorName: reason instanceof Error ? reason.name : undefined,
        // Deliberately does not auto-reload on a ChunkLoadError: the common
        // case here is a <Link> prefetch failing on a stale tab while the
        // current page renders perfectly. Recovery happens in error.tsx when
        // the user actually navigates.
        source: 'unhandled-rejection',
      })
    }
    window.addEventListener('error', onError)
    window.addEventListener('unhandledrejection', onRejection)
    return () => {
      window.removeEventListener('error', onError)
      window.removeEventListener('unhandledrejection', onRejection)
    }
  }, [])

  // Clears the chunk-reload cooldown once this load has stayed up long enough
  // to count as a recovery. error.tsx renders under this layout, so the timer
  // also runs while the error fallback is on screen — settleChunkReload is what
  // knows to leave the marker alone in that case.
  useEffect(() => {
    const timer = setTimeout(settleChunkReload, CHUNK_RELOAD_SETTLE_MS)
    return () => clearTimeout(timer)
  }, [])

  return null
}

export default ClientErrorReporter
